import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";

import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { EmployeeListColumns } from "../../appconfig/DatatableSetting";
import { getAllDetails, postAllData, updateData } from "../../service/ApiService";
import { ADD_EMPLOYEE, GET_LIST_EMPLOYEE, UPDATE_EMPLOYEE, CHANGE_EMPLOYEE_STATUS } from "../../config/ApiConstant";
import { addressTemplate, dateTemplate, nameTemplate } from "../../appconfig/TemplateFieldSetting";
import { validateEmployeeForm } from "../../config/Validate";
import { Card } from "primereact/card";
import moment from "moment";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Badge } from "primereact/badge";
import { Checkbox } from "primereact/checkbox";

const ManageEmployee = () => {
  document.title = "Employee | Nagra Accounting";
  const toast = useRef<any>(null);

  // filters
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);

  // loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  // employee list
  const [employeeList, setEmployeeList] = useState<any>([]);

  // add edit dialog
  const [addDeleteEmployeeDialog, setAddDeleteEmployeeDialog] = useState<boolean>(false);

  // isedit
  const [isEdit, setIsEdit] = useState<boolean>(false);

  // error obj
  const [errors, setErrors] = useState<any>({});

  // employee data
  const [globleEmployeeData, setGlobalEmployeeData] = useState<any>({});

  // menu permissions
  const [menuPermissions, setMenuPermissions] = useState<string[]>([]);

  // Calculate the minimum date for dob
  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() - 14);

  // filter
  const emptyFilters = {
    global: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    email: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    phone: {
      operator: FilterOperator.OR,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // useefect for get user list
  useEffect(() => {
    getEmployeeList();
    window.scrollTo({ top: 0, behavior: "smooth" });

    // remove tax payer selected date from local storage
    localStorage.removeItem("taxpayer_selected_date");
  }, []);

  // get employee list api
  const getEmployeeList = async () => {
    try {
      const response = await getAllDetails(GET_LIST_EMPLOYEE + "?role=Employee");
      const data = response;
      if (data) {
        setEmployeeList(data);
        setPageLoad(true);
      } else {
        setEmployeeList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // clear all filter
  const clearAll = () => {
    setGlobalFieldFilter(null);
    setGlobalFilter(null);
  };

  // global search filter on change
  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilter(value);
  };

  // left toolbar template
  const leftToolbarTemplate = () => (
    <span className="block mr-2 p-input-icon-left">
      <i className="pi pi-search" />
      <InputText type="search" value={globalFilter || ""} onChange={onGlobalFilterChange} placeholder="Search..." />
    </span>
  );

  // right toolbar template
  const rightToolbarTemplate = () => (
    <>
      <Button className="ml-1 common-primary-button" icon="pi pi-plus" label="Add Employee Data" onClick={() => addEditDialog()} />

      <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="ml-3 p-button-outlined common-p-outline-button" />
    </>
  );

  // add edit dialog
  const addEditDialog = async (rowData: any = null) => {

    if (rowData) {
      setGlobalEmployeeData({ ...rowData });

      let permissions: string[] = [];

      if (rowData?.menu_access) {
        permissions = rowData.menu_access.split(",").map((item: string) => item);
        setMenuPermissions(permissions);
      }

      // setMenuPermissions(permissions);

      setIsEdit(true);
    } else {
      setGlobalEmployeeData({});
      setMenuPermissions([]);
      setIsEdit(false);
    }

    setErrors({});
    setAddDeleteEmployeeDialog(true);
  };

  // hide add & edit employee modal
  const hideAddEditEmployeeDialog = () => {
    setAddDeleteEmployeeDialog(false);
    setSubmitLoading(false);
    setMenuPermissions([]);
  };

  const onInputChange = (e: any, name: string) => {
    let val;
    if ((name === "joining_date" && val) || (name === "date_of_birth" && val)) {
      val = moment(val).format("YYYY-MM-DD");
    } else {
      val = e?.target?.value ?? e?.value;
    }

    // format postal code: add space after 3 characters
    if (name === "postal_code" && val) {
      // remove all spaces first
      val = val.replace(/\s+/g, "").toUpperCase();
      // insert space after 3 characters if length > 3
      if (val.length > 3) {
        val = val.slice(0, 3) + " " + val.slice(3);
      }
    }

    setGlobalEmployeeData({ ...globleEmployeeData, [name]: val });
    setErrors({ ...errors, [name]: "" });
  };

  const onPermissionChange = (e: any) => {
    let permissions = [...menuPermissions];

    if (e.checked) {
      permissions.push(e.value);
    } else {
      permissions = permissions.filter((item) => item !== e.value);
    }

    setMenuPermissions(permissions);
  };

  // add & update employee api call
  const submitEmployeeForm = async () => {
    const { isError, errors } = validateEmployeeForm(globleEmployeeData, isEdit, menuPermissions);
    setErrors(errors);
    if (isError) return;

    setSubmitLoading(true);
    try {
      const payload = {
        first_name: globleEmployeeData?.first_name,
        last_name: globleEmployeeData?.last_name,
        email: globleEmployeeData?.email,
        mobile_no: globleEmployeeData?.mobile_no,
        joining_date: globleEmployeeData?.joining_date ? moment(globleEmployeeData?.joining_date).format("YYYY-MM-DD") : null,
        address: globleEmployeeData?.address,
        date_of_birth: globleEmployeeData?.date_of_birth ? moment(globleEmployeeData?.date_of_birth).format("YYYY-MM-DD") : null,
        postal_code: globleEmployeeData?.postal_code,
        city: globleEmployeeData?.city,
        role: "Employee",
        menu_access: menuPermissions.join(","),
      };

      const response = isEdit ? await updateData(UPDATE_EMPLOYEE, globleEmployeeData?.id, payload) : await postAllData(ADD_EMPLOYEE, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        hideAddEditEmployeeDialog();
        getEmployeeList();
        setMenuPermissions([]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // change employee status
  const changeEmployeeStatus = async (rowData: any) => {
    try {
      const newStatus = !rowData.status;
      const payload = {
        status: newStatus,
      };
      const response = await updateData(CHANGE_EMPLOYEE_STATUS, rowData.id, payload);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message || "Status updated successfully",
        });
        getEmployeeList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update status",
      });
    }
  };

  // status body template
  const statusBodyTemplate = (rowData: any) => {
    const isActive = rowData.status;

    return (
      <div className="flex flex-column align-items-center gap-1">
        <InputSwitch
          checked={isActive}
          onChange={() => changeEmployeeStatus(rowData)}
        />

        <Badge
          value={isActive ? "Active" : "Inactive"}
          severity={isActive ? "success" : "danger"}
        />
      </div>
    );;
  };

  // action body template
  const actionBodyTemplate = (rowData: any) =>
    rowData ? (
      <div className="action">
        <Button
          icon="pi pi-pencil"
          tooltip="Edit"
          className="common-edit-btn"
          tooltipOptions={{ position: "bottom" }}
          onClick={() => addEditDialog(rowData)}
          disabled={rowData.status === false}
        />
      </div>
    ) : (
      "-"
    );

  return (
    <>
      <AppBreadcrumb currentMenu="Manage Employee" pageName="Employee List" pageTitle="Employee List" />
      <div className="grid crud-demo">
        <style>
          {`
            .p-inputswitch:not(.p-disabled):hover .p-inputswitch-slider {
              background: #ced4da !important;
            }
            .p-inputswitch.p-inputswitch-checked:not(.p-disabled):hover .p-inputswitch-slider {
              background: #2196f3 !important;
            }
          `}
        </style>
        <div className="col-12">
          <Card className="bg-white lg:p-2 crud-demo">
            <Toast ref={toast} />
            <Toolbar className="gap-3" left={leftToolbarTemplate} right={rightToolbarTemplate} />
            {pageLoad ? (
              <DataTable
                value={employeeList}
                showGridlines
                dataKey="id"
                stripedRows
                globalFilter={globalFilter}
                responsiveLayout={"scroll"}
                rows={defaultRowOptions}
                paginator={employeeList.length > 0}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatble-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries + " Employee"}
                emptyMessage="No Users Found"
                filters={globalFieldFilter}
                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                removableSort
                globalFilterFields={["first_name", "last_name", "email", "phone", "unique_id"]}
              >
                {EmployeeListColumns.map((col, i) => {
                  if (col.field === "name") {
                    return <Column key={col.field} field={col.field} header={col.header} body={nameTemplate} />;
                  } else if (col.field === "action") {
                    return <Column key={col.field} field={col.field} header={col.header} body={actionBodyTemplate} />;
                  } else if (col.field === "email") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        sortable
                        filter
                        filterPlaceholder={"Search by " + col.header}
                        filterField={col.field}
                      />
                    );
                  } else if (col.field === "mobile_no") {
                    return <Column key={col.field} field={col.field} header={col.header} sortable />;
                  } else if (col.field === "joining_date") {
                    return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} sortable />;
                  } else if (col.field === "status") {
                    return <Column key={col.field} field={col.field} header={col.header} body={statusBodyTemplate} />;
                  } else if (col.field === "address") {
                    return <Column key={col.field} field={col.field} header={col.header} body={addressTemplate} />;
                  } else if (col.field === "unique_id") {
                    return <Column key={col.field} field={col.field} header={col.header} />;
                  } else {
                    return <Column key={col.field} field={col.field} header={col.header} />;
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {EmployeeListColumns.map((col) => (
                  <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                ))}
              </DataTable>
            )}
          </Card>
        </div>
      </div>
      {/* Add/Edit Dialog */}
      <Dialog
        visible={addDeleteEmployeeDialog}
        style={{ width: "800px" }}
        className="mx-3 lg:mx-0"
        draggable={false}
        header={isEdit ? "Edit Employee" : "Add Employee"}
        modal
        footer={
          <div className="flex pt-2 align-items-center justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="mr-2 p-button-secondary" onClick={hideAddEditEmployeeDialog} />
            <Button label="Submit" icon="pi pi-check" className="common-primary-button" onClick={submitEmployeeForm} loading={submitLoading} />
          </div>
        }
        onHide={hideAddEditEmployeeDialog}
      >
        <div className="grid p-fluid formgrid">
          {/* First Name */}
          <div className="field col-12 md:col-6">
            <label htmlFor="name">First Name <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.first_name || ""}
                name="first_name"
                placeholder="Enter First Name"
                onChange={(e) => onInputChange(e, "first_name")}
                className={errors["first_name"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["first_name"]}</small>
          </div>

          {/* Last Name */}
          <div className="field col-12 md:col-6">
            <label htmlFor="name">Last Name <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.last_name || ""}
                name="last_name"
                placeholder="Enter Last Name"
                onChange={(e) => onInputChange(e, "last_name")}
                className={errors["last_name"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["last_name"]}</small>
          </div>
          {/* Email */}
          <div className="field col-12 md:col-6">
            <label htmlFor="email">Email <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.email || ""}
                name="email"
                placeholder="Enter Email"
                onChange={(e) => onInputChange(e, "email")}
                className={errors["email"] && "p-invalid"}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["email"]}</small>
          </div>
          {/* Phone */}
          <div className="field col-12 md:col-6">
            <label htmlFor="mobile_no">Phone <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.mobile_no || ""}
                name="mobile_no"
                placeholder="Enter Phone Number"
                onChange={(e) => onInputChange(e, "mobile_no")}
                className={errors["mobile_no"] && "p-invalid"}
                keyfilter="pint"
                maxLength={10}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["mobile_no"]}</small>
          </div>

          {/* Joining Date */}
          <div className="field col-12 md:col-6">
            <label htmlFor="date">Joining Date <span className="required">*</span></label>
            <div className="input-effect-div">
              <Calendar
                value={globleEmployeeData.joining_date ? moment(globleEmployeeData.joining_date, "YYYY-MM-DD").toDate() : null}
                dateFormat="dd/mm/yy"
                placeholder="Enter Joining Date"
                onChange={(e) => onInputChange(e, "joining_date")}
                showIcon
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["joining_date"]}</small>
          </div>

          {/* Date of Birth */}
          <div className="field col-12 md:col-6">
            <label htmlFor="date">Date of Birth <span className="required">*</span></label>
            <div className="input-effect-div">
              <Calendar
                value={globleEmployeeData.date_of_birth ? moment(globleEmployeeData.date_of_birth, "YYYY-MM-DD").toDate() : null}
                dateFormat="dd/mm/yy"
                placeholder="Enter Date Of Birth"
                onChange={(e) => onInputChange(e, "date_of_birth")}
                showIcon
                maxDate={maxDate}
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["date_of_birth"]}</small>
          </div>

          {/* City */}
          <div className="field col-12 md:col-6">
            <label htmlFor="name">City <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.city || ""}
                name="city"
                onChange={(e) => onInputChange(e, "city")}
                className={errors["city"] && "p-invalid"}
                placeholder="Enter City"
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["city"]}</small>
          </div>

          {/* Postal Code */}
          <div className="field col-12 md:col-6">
            <label htmlFor="postal_code">Postal Code <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputText
                value={globleEmployeeData.postal_code || ""}
                name="postal_code"
                onChange={(e) => onInputChange(e, "postal_code")}
                className={errors["postal_code"] && "p-invalid"}
                placeholder="Enter Postal Code"
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["postal_code"]}</small>
          </div>

          {/* Address */}
          <div className="field col-12 md:col-12">
            <label htmlFor="address"> Address <span className="required">*</span></label>
            <div className="input-effect-div">
              <InputTextarea
                rows={3}
                value={globleEmployeeData.address || ""}
                name="address"
                onChange={(e) => onInputChange(e, "address")}
                className={errors["address"] && "p-invalid"}
                placeholder="Enter Address"
              />
              <span className="focus-bg"></span>
            </div>
            <small className="p-invalid-txt">{errors["address"]}</small>
          </div>
          <div className="field col-12">
            <label>Menu Permissions</label>

            <div className="flex flex-wrap gap-3 mt-2">

              {/* Manage Employees */}
              <div className="flex align-items-center">
                <Checkbox
                  inputId="manageEmployees"
                  value="Manage Employees"
                  onChange={onPermissionChange}
                  checked={menuPermissions.includes("Manage Employees")}
                />
                <label htmlFor="manageEmployees" className="ml-2">
                  Manage Employees
                </label>
              </div>

              {/* Tax Payer List */}
              <div className="flex align-items-center">
                <Checkbox
                  inputId="taxPayerList"
                  value="Tax Payer List"
                  onChange={onPermissionChange}
                  checked={menuPermissions.includes("Tax Payer List")}
                />
                <label htmlFor="taxPayerList" className="ml-2">
                  Tax Payer List
                </label>
              </div>

              {/* Company List */}
              <div className="flex align-items-center">
                <Checkbox
                  inputId="companyList"
                  value="Company List"
                  onChange={onPermissionChange}
                  checked={menuPermissions.includes("Company List")}
                />
                <label htmlFor="companyList" className="ml-2">
                  Company List
                </label>
              </div>

              {/* GST Calc */}
              <div className="flex align-items-center">
                <Checkbox
                  inputId="gstCalc"
                  value="GST Calc"
                  onChange={onPermissionChange}
                  checked={menuPermissions.includes("GST Calc")}
                />
                <label htmlFor="gstCalc" className="ml-2">
                  GST Calc
                </label>
              </div>

            </div>

            {/* ✅ Validation Error */}
            <small className="p-invalid-txt">
              {errors["menu_permissions"]}
            </small>
          </div>
        </div>
      </Dialog>
      {/* Delete Dialog */}
    </>
  );
};
export default ManageEmployee;
